import { Sparkles } from "lucide-react";

import { useNotices } from "@/api/queries/guidance";
import type { Notice, Tour } from "@/api/types";
import { Badge, Button, Dialog, PageLoader } from "@/components/ui";
import { formatDate } from "@/lib/format";

const categoryTone = { new: "accent", improved: "info", fixed: "success" } as const;

export function NoticeItem({ notice, tour, onTour }: { notice: Notice; tour?: Tour; onTour?: (key: string) => void }) {
  return (
    <li className="border-b border-line py-3 first:pt-0 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={categoryTone[notice.category as keyof typeof categoryTone] ?? "neutral"}>
          {notice.category.charAt(0).toUpperCase() + notice.category.slice(1)}
        </Badge>
        <span className="text-xs text-fg-subtle tabular">
          v{notice.version} · {formatDate(notice.released_at)}
        </span>
        {notice.unread && <span className="text-xs font-medium text-accent-text">Unread</span>}
      </div>
      <h3 className="mt-1.5 text-sm font-semibold text-fg">{notice.title}</h3>
      <p className="mt-0.5 text-sm text-fg-muted">{notice.body}</p>
      {tour && onTour && (
        <Button size="sm" className="mt-2" onClick={() => onTour(tour.key)}>
          Show me how ({tour.steps.length} steps)
        </Button>
      )}
    </li>
  );
}

/** One dialog for all unread notices (never a stack of competing dialogs). */
export function WhatsNewDialog({
  slug,
  mode,
  unread,
  tours,
  onClose,
}: {
  slug: string;
  mode: "unread" | "history";
  unread: Notice[];
  tours: Tour[];
  onClose: (tourKey?: string) => void;
}) {
  const history = useNotices(slug, false, mode === "history");
  const notices = mode === "unread" ? unread : (history.data ?? []);
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="What's new in K-SHOP"
      description={mode === "unread" ? "Changes since you last looked." : "Recent product updates."}
      footer={
        <Button variant="primary" onClick={() => onClose()}>
          Got it
        </Button>
      }
    >
      {mode === "history" && history.isLoading ? (
        <PageLoader />
      ) : notices.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-fg-muted">
          <Sparkles aria-hidden className="size-4" /> You're up to date.
        </p>
      ) : (
        <ul>
          {notices.map((notice) => (
            <NoticeItem
              key={notice.key}
              notice={notice}
              tour={tours.find((t) => t.key === notice.tour_key)}
              onTour={(key) => onClose(key)}
            />
          ))}
        </ul>
      )}
    </Dialog>
  );
}
